import * as React from 'react'
import whitePixel from 'utils/whitePixel'
import LazyImg from 'theme/atoms/LazyImg'
import genProductImageUrl from 'utils/genProductImageUrl'
import styled from 'styled-components'
import { isBiggerThan } from 'utils/media-size'
type Props = React.HTMLAttributes<HTMLImageElement> & {
  img: {
    url: string
    classes: Array<'ASSET_FS' | 'ASSETS_M' | 'EMG' | string>
  }
  size: 'l' | 'm' | 's' | 'xs'
  alt: string
  base64?: string
  holderRef?: React.RefObject<HTMLDivElement | null>
  'data-cy-state'?: string
  'data-cy-handle-zoom-in'?: string
  'data-cy-handle-zoom-reset'?: string
}
export default function ProductImage({
  img,
  size,
  alt,
  base64,
  ...props
}: Props) {
  if (base64 === undefined) base64 = whitePixel

  const [zoomed, setZoomed] = React.useState(false)
  const [mouseState, setMouseState] = React.useState({
    translateX: 0,
    translateY: 0,
    scale: 1
  })

  const zoomedImageRef = React.useRef<HTMLImageElement>(null)

  const scaleFactor = 1.5
  const { translateX, translateY } = mouseState
  const handleMouseMove = (e: React.MouseEvent) => {
    const { top, left, width, height } = zoomedImageRef.current
      ? zoomedImageRef.current.getBoundingClientRect()
      : { top: 0, left: 0, width: 0, height: 0 }
    const moveX = left - e.clientX < -width / 2 ? -width / 2 : left - e.clientX
    const moveY = top - e.clientY < -height / 2 ? -height / 2 : top - e.clientY

    setMouseState((prevState) => ({
      ...prevState,
      translateX: moveX,
      translateY: moveY
    }))
  }

  const touchMove = (e: React.TouchEvent) => {
    const { top, left } = zoomedImageRef.current
      ? zoomedImageRef.current.getBoundingClientRect()
      : { top: 0, left: 0 }
    setMouseState((prevState) => ({
      ...prevState,
      translateX: left - e.touches[0].clientX,
      translateY: top - e.touches[0].clientY
    }))
  }
  const escFunction = React.useCallback((event) => {
    if (event.key === 'Escape') {
      setZoomed(false)
    }
  }, [])

  React.useEffect(() => {
    document.addEventListener('keydown', escFunction, false)

    return () => {
      document.removeEventListener('keydown', escFunction, false)
    }
  }, [])

  // if whitepixel is used by e.g. dummy products, use base64 instead
  const isWhitePixel = img?.url === whitePixel

  const imgUrl = isWhitePixel ? base64 : genProductImageUrl(img, size)
  const imgUrlWithScale = isWhitePixel
    ? base64
    : genProductImageUrl(img, size, '2')

  const handleZoomStart = () => {
    // IS VALID SCREEN
    if (isBiggerThan('LG')) return
    // SET ZOOM
    setZoomed(true)
  }

  if (zoomed)
    return (
      <ZoomedWrapper
        ref={zoomedImageRef}
        $translateX={translateX}
        $translateY={translateY}
        $scaleFactor={scaleFactor}
        data-cy-state={props['data-cy-state']}
      >
        <LazyImg
          className="ProductImage"
          src={imgUrl}
          alt={alt}
          offset={100}
          base64={base64}
          srcSet={`${imgUrlWithScale}, ${imgUrlWithScale}`}
          onClick={() => setZoomed(false)}
          onMouseMove={handleMouseMove}
          onTouchMove={touchMove}
          data-cy-handle={props['data-cy-handle-zoom-reset']}
        />
      </ZoomedWrapper>
    )

  return (
    <LazyImg
      className="ProductImage"
      src={imgUrl}
      alt={alt}
      offset={100}
      base64={base64}
      srcSet={`${imgUrlWithScale}, ${imgUrlWithScale} 2x`}
      onClick={handleZoomStart}
      data-cy-handle={props['data-cy-handle-zoom-in']}
    />
  )
}

const ZoomedWrapper = styled.div<{
  $translateX: number
  $translateY: number
  $scaleFactor: number
  maxHeight?: number
  maxWidth?: number
}>`
  overflow: hidden;
  max-width: 750px;
  max-height: 750px;
  transform: translateZ(0);
  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: scale ease 0.2s;
    max-width: 750px;
    max-height: 750px;
    transform-origin: 0px 0px 0px;
    transform: ${({ $translateX, $translateY, $scaleFactor }) =>
      `translate(${$translateX}px, ${$translateY}px) scale(${$scaleFactor})`};
  }
`
